import { createContext, useContext, useEffect, useState, type ReactNode } from "react";

/**
 * Density control (docs/dashboard-ux-proposal.md, cross-cutting principles).
 *
 * On-call needs to see everything at once; a reviewer wants breathing room. The
 * provider sets one class on a wrapper and the shared row/cell components read it
 * through CSS custom properties, so density is a single knob rather than a prop
 * threaded through every list.
 */

export type Density = "comfortable" | "compact" | "dense";

export const DENSITIES: Density[] = ["comfortable", "compact", "dense"];

export const DENSITY_CLASS: Record<Density, string> = {
  comfortable: "density-comfortable",
  compact: "density-compact",
  dense: "density-dense",
};

const STORAGE_KEY = "rl_density";

const DensityContext = createContext<Density>("comfortable");

export function useDensity(): Density {
  return useContext(DensityContext);
}

/** The chosen density, persisted per browser so on-call's choice sticks. */
export function useDensityPreference(): [Density, (next: Density) => void] {
  const [density, setDensity] = useState<Density>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      return stored === "compact" || stored === "dense" ? stored : "comfortable";
    } catch {
      return "comfortable";
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, density);
    } catch {
      /* storage is best-effort */
    }
  }, [density]);
  return [density, setDensity];
}

export function DensityProvider({
  value,
  children,
}: {
  value: Density;
  children: ReactNode;
}) {
  return (
    <DensityContext.Provider value={value}>
      <div className={DENSITY_CLASS[value]}>{children}</div>
    </DensityContext.Provider>
  );
}
